import { useTranslation } from 'react-i18next';
import { FC, useContext, useState } from "react";
import StoreImg from "images/login/store.jpg";

import { LoginLayoutWrap } from "./style";
import {
  Button,
  Checkbox,
  Flex,
  Form,
  FormProps,
  Input,
  Select,
  Typography,
  notification,
} from "antd";
import { AREA_CODES } from "utils/const";
import { AreaEnum, User, UserLangEnum, UserPermissionEnum, UserSettingEnum } from "types/user";
import { login, userFetch } from "apis/user";
import { Token } from "@/types/token";
import { setStorage, getStorage, clearStorage } from "@/utils/storage";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/store/index"
import { setCurUser, setLoginStatus } from "@/store/modules/userReducer";
import { setCurOrg } from "@/store/modules/orgReducer";
import useOrg from "@/pages/OrgManager/hooks/useOrgDetail"
import { CommonStateEnum, CustomWindow } from "@/types/common";
import { Org } from '@/types/org';
import { LANGS } from '../HomeLayout/components/Locale';
import i18n from "@/locale";
import errorCodes from '@/apis/error';
import { hasPermission } from '@/utils/user';
import { orgFetchs } from '@/apis/org';
import profileLogout from '@/pages/Profile/utils/logout';
import { isNumber } from 'lodash';
import { UserContext } from '@/layout/context/SettingsContext';

const { Title } = Typography;
const { Option } = Select;

type LoginFieldType = {
  mobile: string;
  password: string;
  remember?: string;
  areaCode: AreaEnum;
  loginOrgId?: string;
};


const LoginLayout: FC = () => {
  const { t } = useTranslation();
  const stateDispatch: AppDispatch = useDispatch()
  const [[, setUser], [, setUserSettings]] = useContext(UserContext);
  const [notificationApi, contextHolder] = notification.useNotification({ placement: 'top' });

  // orgInfo
  const [, , , { fetch }] = useOrg();
  const [loginLoading, setLoginLoading] = useState<boolean>(false);

  // login form
  const [loginForm] = Form.useForm()
  const [areaCode, setAreaCode] = useState<AreaEnum>(AreaEnum.CN);
  const [mobile, setMobile] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isRemember, setIsRememBer] = useState<boolean>(true);
  const lastLoginUser = getStorage('lastLoginUser');
  let orgList: Org[] = [];

  const setToken = (info: Token) => {
    setStorage("accessToken", info.accessToken, 'locale');
    setStorage("refreshToken", info.refreshToken, 'locale');
  };

  const onFinish: FormProps<LoginFieldType>["onFinish"] = (values) => {
    clearStorage();
    setLoginLoading(true);
    login(values.areaCode, values.mobile, values.password)
      .then((res) => {
        setToken(res.body); // 设置 accessToken, refreshToken

        userFetch().then((user) => {
          const langSetting = user.settings.find(setting => setting.type === UserSettingEnum.LANG);
          const userLang =
            LANGS.find(lang => lang.key === langSetting?.value) ??
            LANGS.find(lang => lang.key === UserLangEnum.EN);
          const nextUserSettings = langSetting
            ? user.settings
            : [
              ...user.settings,
              { type: UserSettingEnum.LANG, value: UserLangEnum.EN }
            ];

          setLoginLoading(true);
          stateDispatch(setCurUser(user));
          setUser(user);
          setUserSettings(nextUserSettings);
          setStorage('curUser', user, 'locale');
          if (userLang) {
            setStorage('locale', userLang.key)
            i18n.changeLanguage(userLang.appApply)
          }
          loginFinish(user, values);
        
        }).catch((err) => {
          notificationApi.error({ message: err })
        }).finally(() => setLoginLoading(false))
      })
      .catch((err) => {
        setLoginLoading(false)
        console.log(err)
        switch (err) {
          case 70205:
            notificationApi.error({
              description: t('login.acntNotExist'),
              message: t('login.loginFailed'),
            });
            break;
          case 70206:
          case 70207:
            notificationApi.error({
              message: t('login.phoneDisabledTip'),
            });
            break;
          case 70208:
          case 70258:
            notificationApi.error({
              description: t('login.acntOrPwdError'),
              message: t('login.loginFailed'),
            });
            break;
          default:
            notificationApi.error({ message: t('login.loginFailed'), description: errorCodes[err] || err});
            break;
        }
      })
  };

  const loginFinish = async (user: User, formValues: LoginFieldType) => {
    if(!hasPermission(user, [UserPermissionEnum.ADMIN]) && user.orgs && user.orgs.length === 0) {
      notification.warning({ placement: 'top', message: t('layout.loginFailTip2') });
      profileLogout();
      return;
    }
    if(hasPermission(user, [UserPermissionEnum.ADMIN])) {
      try {
        orgList = (await orgFetchs()).body;
      } catch (error) {
        notification.warning({ placement: 'top',message: t('org.orgFetchFail'), description: isNumber(error) ? errorCodes[error] : error as String});
      }
    } else {
      orgList = user.orgs;
    }
    
    if (isRemember) {
      loginToOrgInfo(user)
      setStorage("lastLoginUser", [{ areaCode: formValues.areaCode, mobile: formValues.mobile, password: formValues.password  }], 'locale');
    } else {
      setStorage("lastLoginUser", [{ areaCode: formValues.areaCode, mobile: formValues.mobile }], 'locale');
      stateDispatch(setLoginStatus(true));
      loginSuccprompt();
      saveUserOrgInfo();
    }
  }

  const onFinishFailed: FormProps["onFinishFailed"] = (
    errorInfo
  ) => {
    if (errorInfo.errorFields.length > 0) {
      return notificationApi.error({
        description: errorInfo.errorFields[0].errors[0],
        message: t('login.checkFailed'),
      });
    }
  };

  // 该组织包含在orgs内、或者有下级权限
  const checkOrgPermission = (org: Org, user: User): boolean => {
    return true
  };

  const loginToOrgInfo = (user: User) => {
    const accountStorage = getStorage("account");
      /*
        判断当前mobile若在account内存在, loginOrgId存在且状态正常则无需向缓存修改数据，直接获取loginOrgId进行登录；
        若loginOrgId被删除/禁用则选择下一个组织登录；loginOrgId字段不存在则需进行修改缓存account数据。
      */
      if (accountStorage && accountStorage?.length > 0) {
        const foundItem = accountStorage?.find((item: LoginFieldType) => item.mobile === mobile)
        if (foundItem) {
          if (foundItem?.loginOrgId) {
            fetch(foundItem.loginOrgId).then((res) => {
              if (res && res.state === CommonStateEnum.ACTIVE && checkOrgPermission(res, user)) {
                stateDispatch(setCurOrg(res))
                setStorage('curOrgId', foundItem.loginOrgId, 'locale')
                stateDispatch(setLoginStatus(true))
                loginSuccprompt()
              } else {
                updateStorageAccount(true, accountStorage)
              }
            }).catch((err) => {
              if (err === 70301) {
                if (user.orgs && user.orgs.length > 0) {
                  updateStorageAccount(true, accountStorage)
                } else {
                  notification.warning({ placement: 'top', message: t('login.orgTip1') })
                  stateDispatch(setCurOrg(null))
                }
              } else {
                notificationApi.error({message: errorCodes[err]})
              }
            })
          } else {
            updateStorageAccount(true, accountStorage)
          }
        } else {
          updateStorageAccount(false, [...accountStorage, { areaCode, mobile, password, loginOrgId: orgList && orgList.length > 0 ? orgList[0].id : ''}])
        }
      } else {
        updateStorageAccount(false, [{ areaCode, mobile, password, loginOrgId: orgList && orgList.length > 0 ? orgList[0].id : '' }])
      }
    
  }

  const updateStorageAccount = (isNeedMobile: Boolean, accountArray: Array<LoginFieldType>) => {
    if (isNeedMobile) {
      accountArray.forEach((element: LoginFieldType) => {
        if (element.mobile === mobile && orgList) {
          element.loginOrgId = orgList[0]?.id;
        }
      });
    }
    saveUserOrgInfo();
    stateDispatch(setLoginStatus(true));
    setStorage("account", accountArray, 'locale');
    loginSuccprompt();
  }

  const saveUserOrgInfo = () => {
    if(orgList && orgList.length > 0) {
      setStorage('curOrgId', orgList[0].id, 'locale')
      stateDispatch(setCurOrg(orgList[0]))
    }
  }

  const loginSuccprompt = () => {
    notificationApi.success({
      description: t('login.toHome'),
      message: t('login.loginSuccess'),
    });
  }

  const prefixSelector = (
    <Form.Item name="areaCode" noStyle>
      <Select
        value={areaCode}
        onChange={setAreaCode}
        style={{ width: 80 }}
        popupMatchSelectWidth={false}
        labelRender={(label) => label.value}
      >
        {AREA_CODES.map((area) => (
          <Option
            key={area.code}
            value={area.code}
          >{`${area.code} ${area.area}`}</Option>
        ))}
      </Select>
    </Form.Item>
  );

  return (
    <LoginLayoutWrap>
      {contextHolder}
      <div className="layout--left">
        <img src={StoreImg} alt={t('login.carousel')} />
      </div>
      <Flex vertical align="start" justify="center" className="layout--right">
        <div className="layout--right--header">
          <Title>{(window as unknown as CustomWindow).config.server_name} WMS</Title>
        </div>
        <Form
          form={loginForm}
          style={{ width: "100%", textAlign: "right" }}
          name="login"
          initialValues={{
            remember: true,
            areaCode: AreaEnum.CN,
            ...lastLoginUser?.[0]
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item<LoginFieldType>
            label={t('login.acnt')}
            name="mobile"
            rules={[{ required: true, message: t('login.enterAcnt') }]}
          >
            <Input
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder={t('login.enterAcnt')}
              addonBefore={prefixSelector}
            />
          </Form.Item>

          <Form.Item<LoginFieldType>
            label={t('login.pwd')}
            name="password"
            rules={[{ required: true, message: t('login.enterPwd') }]}
          >
            <Input.Password
              value={password}
              placeholder={t('login.enterPwd')}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item<LoginFieldType>
            name="remember"
            valuePropName="checked"
            wrapperCol={{ offset: 8, span: 16 }}
          >
            <Checkbox
              value={isRemember}
              onChange={(e) => setIsRememBer(e.target.checked)}
            >
              {t('login.remberPwd')}
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={loginLoading}>
              {t('login.login')}
            </Button>
          </Form.Item>
        </Form>
      </Flex>
    </LoginLayoutWrap>
  );
};

export default LoginLayout;
